import { useState } from 'react';
import styled from 'styled-components/macro';
import classNames from 'classnames';
import { ReactComponent as Pin } from '../../assetsFr/pin.svg';
import { ReactComponent as ArrowRight } from '../../assetsFr/arrow-right.svg';
import { ProductType } from '../../config/products';
import useTranslations from '../../components/utils/language-provider';
import { usePopperTooltip } from 'react-popper-tooltip';
import NewFlag from './newflag';

type ProductPinProps = {
    productType: ProductType;
    showNewFlag?: boolean;
    style?: React.CSSProperties;
    className?: string;
    popoverOpenDefault?: boolean;
}

const PinInner = styled.div`
    position: relative;
    z-index: 2;
`

const PinWrapper = styled.div`
    position: relative;
    z-index: 2;
    color: #fff;
    margin-top: -53px;
    margin-left: -17px;

    @media ${props => props.theme.screens.lg} {
        margin-top: -109px;
        margin-left: -39px;
    }

    &.front {
        z-index: 3000;
    }

    ${PinInner}:hover {
        cursor: pointer;

        .pin {
            color: ${props => props.theme.colors.primaryLight};
        }

        .arrow {
            top: 12px;
        }
    }

    .pin {
        width: 35px;
        color: ${props => props.theme.colors.primary};
        transition: all 100ms ease-in;

        @media ${props => props.theme.screens.lg} {
            width: 75px;
        }

        &:hover {
            cursor: pointer;
            color: ${props => props.theme.colors.primaryLight};

            + .arrow {
                top: 12px;
            }
        }
    }

    .arrow {
        position: absolute;
        top: 10px;
        left: 50%;
        height: 15px;
        transform: rotate(-90deg);
        margin-left: -4px;
        transition: all 100ms ease-in;

        @media ${props => props.theme.screens.lg} {
            top: 15px;
            height: 40px;
            margin-left: -10px;
        }
    }
`

const Arrow = styled.div`
    width: 33px;
    height: 20px;

    @media ${props => props.theme.screens.lg} {
        width: 56px;
        height: 40px;
    }
`

const ArrowInner = styled.div`
    width: 100%;
    height: 100%;
    background: #fff;
    transform: scaleY(0);
    transform-origin: bottom;
    transition: transform 300ms;
`

const MenuItem = styled.li`
    white-space: nowrap;
    box-shadow: 0 5px 10px rgba(0,0,0,.6);
`

const MenuItemHead = styled(MenuItem)`
    padding: 4px 12px 3px;
    background: ${props => props.theme.colors.primary};
    font-weight: bold;
    color: #fff;
    font-size: 18px;

    @media ${props => props.theme.screens.lg} {
        padding: 9px 23px 7px;
        font-size: 26px;
    }
`

const MenuLink = styled.a`
    display: block;
    padding: 4px 5px 3px;
    background: #fff;
    border-bottom: 1px solid ${props => props.theme.colors.primary};
    font-size: 12px;
    transition: background 100ms ease-in;
    cursor: pointer;
    color: #000;
    text-decoration: none;

    @media ${props => props.theme.screens.lg} {
        padding: 8px 10px 7px;
        font-size: 14px;
    }

    &:hover {
        color: #fff;
        background-color: ${props => props.theme.colors.primary};
    }
`

const Menu = styled.ul`
    text-align: center;
    list-style: none;
    margin: 0;
    padding: 0;
`

const Popper = styled.div`
    ${MenuItem},
    ${MenuItemHead} {
        position: relative;
        top: 20px;
        opacity: 0;
        transition: top 200ms, opacity 200ms;
    }

    &.open {
        ${MenuItem},
        ${MenuItemHead} {
            opacity: 1;
            top: 0;
        }

        ${ArrowInner} {
            transform: scaleY(1);
        }
    }
`

const ProductPin = ({
    style = {},
    className = '',
    showNewFlag = false,
    popoverOpenDefault = false,
    productType,
}: ProductPinProps) => {
    const [popoverOpen, setPopoverOpen] = useState(popoverOpenDefault);
    const [menuOpen, setMenuOpen] = useState(popoverOpenDefault);
    const translations = useTranslations();
    const product = translations.products[productType];

    const {
        getArrowProps,
        getTooltipProps,
        setTooltipRef,
        setTriggerRef,
        visible,
    } = usePopperTooltip({
        placement: 'top',
        trigger: 'click',
        visible: popoverOpen,
        onVisibleChange(state) {
            if (state === false) {
                handleClose();
            }
        }
    });

    const handleClose = () => {
        setPopoverOpen(false);
        setMenuOpen(false);

        window.dispatchEvent(new CustomEvent('debug-product-popover', {detail: {productType: undefined}}));
    }

    const handleOpen = () => {
        setPopoverOpen(true);
        window.dispatchEvent(new CustomEvent('debug-product-popover', {detail: {productType}}));

        setTimeout(() => {
            setMenuOpen(true);
        }, 100);
    }

    if (!product) {
        return null;
    }

    return (
        <>
            <PinWrapper style={style} className={classNames(className, {front: menuOpen})}>
                <PinInner ref={setTriggerRef} onClick={() => handleOpen()}>
                    <Pin className="pin" />
                    <ArrowRight className="arrow" />
                </PinInner>

                {showNewFlag &&
                    <NewFlag type='product'>
                        {translations.buildingDetails.new}
                    </NewFlag>
                }

                {visible &&
                    <Popper ref={setTooltipRef} {...getTooltipProps()} className={classNames({open: menuOpen})}>
                        <Menu>
                            <MenuItemHead style={{transitionDelay: `${product.links.length * 100 + 100}ms`}}>
                                {product.name}
                            </MenuItemHead>

                            {product.links.map((link, index) => (
                                <MenuItem key={link.text + index} style={{transitionDelay: `${(product.links.length - index) * 100 }ms`}}>
                                    <MenuLink href={link.href}>
                                        {link.text}
                                    </MenuLink>
                                </MenuItem>
                            ))}
                        </Menu>
                        <Arrow {...getArrowProps()}>
                            <ArrowInner />
                        </Arrow>
                    </Popper>
                }
            </PinWrapper>
        </>
    )
}

export default ProductPin;
